import React, { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Box, Text, SimpleGrid, Badge, Spinner, VStack, HStack,
} from '@chakra-ui/react';
import { riskApi } from '../services/api';
import { useLanguage } from '../hooks/useLanguage';
import { getRiskLevelStyle, getRiskLevelLabel } from '../utils/riskLevel';
import { Risk } from '../types';

interface RiskDetailModalProps {
  riskCode: string | null;
  fiscalYearId?: number;
  isOpen: boolean;
  onClose: () => void;
}

const RiskDetailModal: React.FC<RiskDetailModalProps> = ({ riskCode, fiscalYearId, isOpen, onClose }) => {
  const { t } = useLanguage();
  const [risk, setRisk] = useState<Risk | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !riskCode) {
      setRisk(null);
      setError(null);
      return;
    }

    const fetchRisk = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await riskApi.getByRiskCode(riskCode, fiscalYearId);
        setRisk(res.data);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load risk');
      } finally {
        setLoading(false);
      }
    };

    fetchRisk();
  }, [riskCode, fiscalYearId, isOpen]);

  const levelStyle = risk
    ? getRiskLevelStyle(risk.inherent_likelihood, risk.inherent_impact)
    : { bg: 'gray.100', text: 'black' };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="800px">
        <ModalHeader bg="blue.800" color="white">
          {t('common.riskCode')}: {riskCode}
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody p={6}>
          {loading && (
            <VStack py={10}>
              <Spinner size="xl" color="blue.500" />
              <Text>{t('common.loading')}</Text>
            </VStack>
          )}

          {error && (
            <Box py={10} textAlign="center">
              <Text color="red.500">{error}</Text>
            </Box>
          )}

          {risk && (
            <VStack spacing={4} align="stretch">
              {/* Risk Info */}
              <Box bg="gray.50" p={4} borderRadius="md">
                <Text fontWeight="bold" fontSize="lg" mb={2}>{risk.risk_name}</Text>
                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                  <Text fontSize="sm"><b>{t('common.riskCode')}:</b> {risk.risk_code}</Text>
                  <Text fontSize="sm"><b>{t('common.department')}:</b> {risk.department ? (risk.department.work_unit ? `${risk.department.department_name} - ${risk.department.work_unit}` : risk.department.department_name) : risk.unit_id}</Text>
                  <Text fontSize="sm"><b>{t('common.riskType')}:</b> {risk.risk_type?.name_th || '-'}</Text>
                  <Text fontSize="sm"><b>{t('common.strategy')}:</b> {risk.strategy?.name || '-'}</Text>
                  <Text fontSize="sm"><b>{t('common.owner')}:</b> {risk.risk_owner || '-'}</Text>
                  <Text fontSize="sm"><b>{t('common.fiscalYear')}:</b> {risk.fiscal_year?.year || '-'}</Text>
                </SimpleGrid>
              </Box>

              {/* Risk Scores */}
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                <Box textAlign="center" p={3} bg="gray.50" borderRadius="md">
                  <Text fontSize="xs" color="gray.500">{t('common.likelihood')}</Text>
                  <Text fontWeight="bold" fontSize="xl">{risk.inherent_likelihood}</Text>
                </Box>
                <Box textAlign="center" p={3} bg="gray.50" borderRadius="md">
                  <Text fontSize="xs" color="gray.500">{t('common.impact')}</Text>
                  <Text fontWeight="bold" fontSize="xl">{risk.inherent_impact}</Text>
                </Box>
                <Box textAlign="center" p={3} bg={levelStyle.bg} color={levelStyle.text} borderRadius="md">
                  <Text fontSize="xs">{t('common.inherent')}</Text>
                  <Text fontWeight="bold" fontSize="xl">{getRiskLevelLabel(risk.inherent_likelihood, risk.inherent_impact)}</Text>
                </Box>
                <Box textAlign="center" p={3} bg="blue.50" borderRadius="md">
                  <Text fontSize="xs" color="gray.500">{t('common.residual')}</Text>
                  <Text fontWeight="bold" fontSize="xl">{risk.residual_risk ? getRiskLevelLabel(risk.residual_likelihood ?? 0, risk.residual_impact ?? 0) : '-'}</Text>
                </Box>
              </SimpleGrid>

              {/* ERM Level */}
              {risk.erm_level && (
                <HStack>
                  <Text fontSize="sm" fontWeight="bold">{t('common.ermLevel')}:</Text>
                  <Badge colorScheme={risk.erm_level === 'faculty' ? 'purple' : 'gray'}>
                    {risk.erm_level === 'faculty' ? 'ระดับคณะ (ERM)' : 'ระดับหน่วยงาน'}
                  </Badge>
                </HStack>
              )}

              {/* Impact Info */}
              {(risk.impact_type || risk.impact_to) && (
                <Box bg="gray.50" p={3} borderRadius="md">
                  {risk.impact_type && <Text fontSize="sm"><b>{t('common.impactType')}:</b> {risk.impact_type}</Text>}
                  {risk.impact_to && <Text fontSize="sm"><b>{t('common.impactTo')}:</b> {risk.impact_to}</Text>}
                </Box>
              )}

              {/* Causes */}
              {risk.causes && risk.causes.length > 0 && (
                <Box>
                  <Text fontWeight="bold" mb={2}>{t('common.causes')} ({risk.causes.length})</Text>
                  {risk.causes.map((cause, idx) => (
                    <Box key={cause.id} p={3} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" mb={2}>
                      <Text fontSize="sm" fontWeight="bold">{cause.cause_id}: {cause.cause_description}</Text>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Internal Controls */}
              {risk.internal_controls && risk.internal_controls.length > 0 && (
                <Box>
                  <Text fontWeight="bold" mb={2}>{t('common.internalControls')} ({risk.internal_controls.length})</Text>
                  {risk.internal_controls.map((ic) => (
                    <Box key={ic.id} p={3} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" mb={2}>
                      <Text fontSize="sm" fontWeight="bold">{ic.ic_no}: {ic.control_description}</Text>
                      <Text fontSize="xs" color="gray.500">{t('common.owner')}: {ic.ic_owner || '-'}</Text>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Additional Controls */}
              {risk.additional_controls && risk.additional_controls.length > 0 && (
                <Box>
                  <Text fontWeight="bold" mb={2}>{t('section.additionalControls', { n: risk.additional_controls.length }).replace(/\{n\}/, String(risk.additional_controls.length))}</Text>
                  {risk.additional_controls.map((ac) => (
                    <Box key={ac.id} p={3} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" mb={2}>
                      <Text fontSize="sm" fontWeight="bold">{ac.ac_no}: {ac.control_description}</Text>
                      <Text fontSize="xs" color="gray.500">{t('common.owner')}: {ac.ac_owner || '-'}</Text>
                    </Box>
                  ))}
                </Box>
              )}

              {/* KRI */}
              {risk.kri_records && risk.kri_records.length > 0 && (
                <Box>
                  <Text fontWeight="bold" mb={2}>{t('common.kri')} ({risk.kri_records.length})</Text>
                  {risk.kri_records.map((kri) => (
                    <Box key={kri.id} p={3} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" mb={2}>
                      <Text fontSize="sm" fontWeight="bold">{kri.kri_name}</Text>
                      <Text fontSize="xs" color="gray.500">{t('common.owner')}: {kri.kri_owner || '-'} | {t('section.result')}: {kri.actual_result || '-'}</Text>
                    </Box>
                  ))}
                </Box>
              )}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default RiskDetailModal;
